/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { ShopperCustomers } from '@/scapi';
import { ToggleCard, ToggleCardEdit, ToggleCardSummary } from '@/components/toggle-card';
import { Button } from '@/components/buttons';
import { Form } from '@/components/forms/form';
import { useBasket } from '@/providers/basket';
import { useAuth } from '@/providers/auth';
import { createShippingAddressSchema, type ShippingAddressData } from '@/lib/checkout/schemas';
import { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import { useScapiFetcher } from '@/hooks/use-scapi-fetcher';
import { useScapiFetcherEffect } from '@/hooks/use-scapi-fetcher-effect';
import { encodeResource, RESOURCE_API_ROUTE } from '@/lib/scapi/resource-encoding';
import {
    getShippingAddressFromCustomer,
    getAddressBookFromCustomer,
    getContactInfoFromCustomer,
    type AddressBookItem,
} from '@/lib/customer/profile-utils';
import { AddressFormFields } from '@/components/address-form-fields';
import SavedAddressesList from './saved-addresses-list';
import AddressModal from './address-modal';
import { AddressValidationModal } from './address-validation-modal';
import SmsConsent from './sms-consent';
import { UITarget } from '@/targets/ui-target';
import type { CheckoutActionData } from '../types';
import {
    addressToFormData,
    findMatchingSavedAddressId,
    getAddressKey,
    isShippingAddressIncomplete,
    normalizeUsPostalCode,
    toCustomerAddressBody,
} from '@/lib/address/address-utils';
import CheckoutErrorBanner from './checkout-error-banner';
import { getCheckoutDisplayError } from '../utils/checkout-display-error';
import ShippingAddressDisplay from './shipping-address-display';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { DEFAULT_COUNTRY_CODE } from '@/components/customer-address-form/constants';
import { stripCountryCode, extractCountryCode, normalizeUsPhone } from '@/lib/address/phone-utils';
import type { AppConfig } from '@/types/config';
import { createLogger } from '@/lib/logger';

const logger = createLogger({ component: 'checkout/shipping-address' });

const FORMAT_URL_PREFIX = '/resource/experian/address/format/';
const VALIDATE_URL = '/resource/experian/address/validate';

/** Normalized, case-insensitive, whitespace-tolerant address-field comparison. */
function normalizeAddressField(value: string | undefined | null): string {
    return (value ?? '').trim().toLowerCase();
}

type ExperianValidationResult = {
    result?: {
        confidence?: string;
        address?: {
            address_line_1?: string;
            address_line_2?: string;
            locality?: string;
            region?: string;
            postal_code?: string;
        };
        suggestions?: Array<{ text: string; global_address_key: string }>;
    };
};

type CheckoutOriginalAddress = {
    firstName: string;
    lastName: string;
    address1: string;
    address2?: string;
    city: string;
    stateCode?: string;
    postalCode?: string;
};

interface ShippingAddressProps {
    onSubmit: (formData: FormData) => void;
    isLoading: boolean;
    actionData?: CheckoutActionData;
    // Step state managed by container
    isCompleted: boolean;
    isEditing: boolean;
    onEdit: () => void;
    // @sfdc-extension-block-start SFDC_EXT_MULTISHIP
    enableMultiAddress: boolean;
    handleToggleShippingAddressMode: () => void;
    // @sfdc-extension-block-end SFDC_EXT_MULTISHIP
}

export default function ShippingAddress({
    onSubmit,
    isLoading,
    actionData,
    isCompleted,
    isEditing,
    onEdit,
    // @sfdc-extension-block-start SFDC_EXT_MULTISHIP
    enableMultiAddress,
    handleToggleShippingAddressMode,
    // @sfdc-extension-block-end SFDC_EXT_MULTISHIP
}: ShippingAddressProps) {
    const cart = useBasket();
    const customerProfile = useCustomerProfile();
    const auth = useAuth();
    const customerId = auth?.customerId;
    const config = useConfig<AppConfig>();
    const { t } = useTranslation('checkout');
    const experianConfig = (
        config.features as {
            experianAddressApi?: { enabled?: boolean; verifyOnSubmit?: boolean; countryCode?: string };
        }
    )?.experianAddressApi;
    const verifyOnSubmit = experianConfig?.enabled && experianConfig?.verifyOnSubmit;
    // @sfdc-extension-line SFDC_EXT_MULTISHIP
    const { t: tMultiship } = useTranslation('extMultiship');

    const shippingAddress = cart?.shipments?.[0]?.shippingAddress;

    // Get auto-populated shipping address from customer profile
    const customerShippingAddress = getShippingAddressFromCustomer(customerProfile);

    // Get phone from contact info (prioritize this for auto-population)
    const contactInfoPhone = cart?.customerInfo?.phone;

    // Customer-profile phone (phoneHome -> phoneBusiness -> phoneMobile). Needed
    // for the registered-user-with-no-saved-addresses case: getShippingAddressFromCustomer
    // returns {} when customerProfile.addresses is empty, so without this fallback the
    // Phone field would render blank even though the profile has a usable number.
    const profilePhone = getContactInfoFromCustomer(customerProfile).phone;

    // Phone priority: saved shipping phone > contact info phone > saved-address phone > profile phone
    const prioritizedPhoneNumber = (shippingAddress?.phone ||
        contactInfoPhone ||
        customerShippingAddress.phone ||
        profilePhone ||
        '') as string;

    const rawSavedAddresses = getAddressBookFromCustomer(customerProfile);
    const hasSavedAddresses = rawSavedAddresses.length > 0;

    // Phone moved to the shipping-address step (was on contact-info). For the
    // inline form path (guest shoppers, who do NOT have a saved-address book to
    // pick from), the schema requires phone — the field is rendered with an
    // asterisk and a 10-digit minimum. The saved-address path doesn't use this
    // schema at all (it re-submits the already-persisted address via
    // `addressToFormData`), so we only need the strict variant here.
    const schema = useMemo(
        () => createShippingAddressSchema(t as unknown as TFunction, { requirePhone: !hasSavedAddresses }),
        [t, hasSavedAddresses]
    );
    const shippingFormError = getCheckoutDisplayError(actionData, 'shippingAddress');

    const form = useForm<ShippingAddressData>({
        resolver: zodResolver(schema),
        defaultValues: {
            firstName: shippingAddress?.firstName || customerShippingAddress.firstName || '',
            lastName: shippingAddress?.lastName || customerShippingAddress.lastName || '',
            address1: shippingAddress?.address1 || customerShippingAddress.address1 || '',
            address2: shippingAddress?.address2 || customerShippingAddress.address2 || '',
            city: shippingAddress?.city || customerShippingAddress.city || '',
            stateCode: shippingAddress?.stateCode || customerShippingAddress.stateCode || '',
            postalCode: shippingAddress?.postalCode || customerShippingAddress.postalCode || '',
            phoneCountryCode: extractCountryCode(prioritizedPhoneNumber) || '+1',
            phone: stripCountryCode(prioritizedPhoneNumber),
            // CHECKOUT-09: restore the persisted opt-in from the basket so a shopper who
            // already ticked the box, got redirected to /cart (CHECKOUT-07 zip-recalc
            // verdict), and returned to /checkout doesn't see the box unchecked. The
            // basket carries `c_smsOptIn` from the previous shipping-address submit; if
            // we re-defaulted to `false` and the shopper submitted shipping again, the
            // action's `c_smsOptIn:false` PATCH would overwrite the persisted `true` and
            // the order would be placed with `c_smsOptIn:false` → `afmIsOkToText:false`
            // (OrderAttributionHelper.applyExportAttribution), losing the TCPA opt-in.
            smsConsent: (cart as { c_smsOptIn?: boolean } | undefined)?.c_smsOptIn === true,
        },
    });

    // SSF-643 — keep Edit form values in sync with the live basket shipping address.
    // react-hook-form only applies `defaultValues` on mount; a mid-page basket refresh
    // (e.g. checkout cart-freshness after a hybrid ZIP sync mutated the address) would
    // otherwise leave Edit showing a stale full address while Summary reads the basket.
    // Skip while the shopper is actively editing so we do not clobber in-progress input.
    const basketShippingAddressKey = useMemo(
        () => (shippingAddress ? getAddressKey(shippingAddress) : ''),
        [shippingAddress]
    );
    useEffect(() => {
        if (isEditing) return;
        const phoneSource = (shippingAddress?.phone ||
            contactInfoPhone ||
            customerShippingAddress.phone ||
            profilePhone ||
            '') as string;
        form.reset({
            firstName: shippingAddress?.firstName || customerShippingAddress.firstName || '',
            lastName: shippingAddress?.lastName || customerShippingAddress.lastName || '',
            address1: shippingAddress?.address1 || customerShippingAddress.address1 || '',
            address2: shippingAddress?.address2 || customerShippingAddress.address2 || '',
            city: shippingAddress?.city || customerShippingAddress.city || '',
            stateCode: shippingAddress?.stateCode || customerShippingAddress.stateCode || '',
            postalCode: shippingAddress?.postalCode || customerShippingAddress.postalCode || '',
            phoneCountryCode: extractCountryCode(phoneSource) || '+1',
            phone: stripCountryCode(phoneSource),
            smsConsent: (cart as { c_smsOptIn?: boolean } | undefined)?.c_smsOptIn === true,
        });
        // basketShippingAddressKey is the intentional trigger; other deps are read for reset values.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when basket address identity changes
    }, [basketShippingAddressKey, isEditing]);

    const currentAddressId = findMatchingSavedAddressId(shippingAddress, rawSavedAddresses);
    const defaultSelectedId =
        currentAddressId ?? rawSavedAddresses.find((a) => a.preferred)?.id ?? rawSavedAddresses[0]?.id ?? '';

    const savedAddresses = useMemo(() => {
        if (!currentAddressId) return rawSavedAddresses;
        const idx = rawSavedAddresses.findIndex((a) => a.id === currentAddressId);
        if (idx <= 0) return rawSavedAddresses;
        const reordered = [...rawSavedAddresses];
        const [match] = reordered.splice(idx, 1);
        reordered.unshift(match);
        return reordered;
    }, [rawSavedAddresses, currentAddressId]);

    const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
    const effectiveSelectedId = selectedAddressId ?? defaultSelectedId;

    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [editingAddress, setEditingAddress] = useState<AddressBookItem | null>(null);
    const pendingEditAddressRef = useRef<FormData | null>(null);
    const pendingAddAddressRef = useRef(false);
    const isEditMode = editingAddress !== null;
    // Tracks which surface opened the AddressValidationModal so handleValidationModalAccept knows
    // whether to use the parent-form values (inline-form path) or the cached AddressModal payload
    // (logged-in Add New / Edit Saved path). Resets in clearValidationModal.
    const [validationSource, setValidationSource] = useState<'inlineForm' | 'addressModal' | null>(null);
    // AddressModal-path payload captured at Save click; held while the AddressValidationModal is
    // up so accept handlers can patch (suggested) and commit with c_experianBypass=true.
    const pendingModalAddressDataRef = useRef<ShopperCustomers.schemas['CustomerAddress'] | null>(null);
    const [isModalValidating, setIsModalValidating] = useState(false);
    const [validationModalOpen, setValidationModalOpen] = useState(false);
    const [validationNoMatch, setValidationNoMatch] = useState(false);
    const [validationSuggestedAddress, setValidationSuggestedAddress] = useState<
        { address1: string; address2?: string; city?: string; stateCode?: string; postalCode?: string } | undefined
    >(undefined);
    const [validationSuggestions, setValidationSuggestions] = useState<
        Array<{ text: string; global_address_key: string }>
    >([]);
    const [originalAddressSnapshot, setOriginalAddressSnapshot] = useState<CheckoutOriginalAddress>({
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        city: '',
        stateCode: '',
        postalCode: '',
    });
    const lastValidationResultRef = useRef<string>('');
    const skipNextModalRef = useRef(false);

    const updateAddressFetcher = useScapiFetcher('shopperCustomers', 'updateCustomerAddress', {
        params: {
            path: {
                customerId: customerId ?? '',
                addressName: editingAddress?.id ?? '',
            },
        },
        body: {} as ShopperCustomers.schemas['CustomerAddress'],
    });

    const applyPendingAddressToBasket = () => {
        if (pendingEditAddressRef.current) {
            onSubmit(pendingEditAddressRef.current);
            pendingEditAddressRef.current = null;
            setEditingAddress(null);
            setAddressModalOpen(false);
        }
    };

    const handleUpdateError = () => {
        pendingEditAddressRef.current = null;
        setEditingAddress(null);
        // Keep modal open on error so user can retry
        // TODO: Add error alert to the user
    };

    useScapiFetcherEffect(updateAddressFetcher, {
        onSuccess: applyPendingAddressToBasket,
        onError: handleUpdateError,
    });

    useEffect(() => {
        if (pendingAddAddressRef.current && !isLoading) {
            // Only close modal if there were no errors
            if (!actionData?.error && !actionData?.fieldErrors) {
                pendingAddAddressRef.current = false;
                setAddressModalOpen(false);
            } else {
                // There was an error, keep modal open but reset flag so user can retry
                pendingAddAddressRef.current = false;
            }
        }
    }, [isLoading, actionData]);

    // The AddressModal stays "saving" while client-side validation is in flight or while the
    // AddressValidationModal is layered on top of it (so the underlying modal can't be closed
    // / re-submitted, and the Save button shows the saving state).
    const isAddressModalAwaitingValidation =
        isModalValidating || (validationSource === 'addressModal' && validationModalOpen);
    const isModalSaving = isEditMode
        ? updateAddressFetcher.state === 'submitting' ||
          pendingEditAddressRef.current !== null ||
          isAddressModalAwaitingValidation
        : (pendingAddAddressRef.current && isLoading) || isAddressModalAwaitingValidation;

    const handleEditAddress = (addressId: string) => {
        const address = savedAddresses.find((a) => a.id === addressId);
        if (!address) return;
        setEditingAddress(address);
        setAddressModalOpen(true);
    };

    /**
     * Commit the AddressModal payload to the appropriate SCAPI surface. When `experianBypass`
     * is true, both the customer-address PATCH (Edit) and the basket shipping-address PUT (Add /
     * Edit follow-up) receive `c_experianBypass=true`, which tells the server hooks to skip
     * Experian and clear pending state. The AddressModal stays open here (it closes only when
     * the success effects fire in response to actionData / fetcher state).
     */
    const commitAddressModalSave = useCallback(
        (data: ShopperCustomers.schemas['CustomerAddress'], experianBypass = false) => {
            // Legacy parity: SFCC stores `phone` as `NNN-NNN-NNNN` and
            // `postal-code` as the 5-digit US ZIP. The modal builds
            // `+1 (NNN) NNN-NNNN` and Experian returns ZIP+4 — normalize BEFORE
            // stamping to FormData (Add path via server action) or to the
            // direct customer-address PATCH body (Edit path).
            const normalizedData: ShopperCustomers.schemas['CustomerAddress'] = {
                ...data,
                phone: normalizeUsPhone(data.phone),
                postalCode: normalizeUsPostalCode(data.postalCode) ?? data.postalCode,
            };

            const formData = new FormData();
            for (const [key, value] of Object.entries(normalizedData)) {
                if (value == null) continue;
                formData.append(key, String(value));
            }
            if (experianBypass) {
                formData.append('experianBypass', 'true');
            }

            if (isEditMode && customerId && editingAddress?.id) {
                pendingEditAddressRef.current = formData;
                // c_experianBypass is honored by the customer-address PATCH hook (one-shot;
                // stripped before persist). It's not on the strict SCAPI schema, so we cast
                // to `any` to attach it — same pattern as CustomerAddressForm.form.tsx.
                const submitData: ShopperCustomers.schemas['CustomerAddress'] = experianBypass
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      ({ ...normalizedData, c_experianBypass: true } as any)
                    : normalizedData;
                void updateAddressFetcher.submit(submitData);
            } else {
                pendingAddAddressRef.current = true;
                onSubmit(formData);
            }
        },
        [isEditMode, customerId, editingAddress?.id, updateAddressFetcher, onSubmit]
    );

    /**
     * Open the AddressValidationModal layered on top of the AddressModal for the AddressModal
     * save path. Captures the payload + original-address snapshot so accept handlers can either
     * commit the original (with bypass) or patch with the suggested fields and commit.
     */
    const openValidationModalForAddressModal = useCallback(
        (
            data: ShopperCustomers.schemas['CustomerAddress'],
            state: {
                noMatch?: boolean;
                suggestedAddress?: {
                    address1: string;
                    address2?: string;
                    city?: string;
                    stateCode?: string;
                    postalCode?: string;
                };
                suggestions?: Array<{ text: string; global_address_key: string }>;
            }
        ) => {
            pendingModalAddressDataRef.current = data;
            setValidationSource('addressModal');
            setOriginalAddressSnapshot({
                firstName: data.firstName ?? '',
                lastName: data.lastName ?? '',
                address1: data.address1 ?? '',
                address2: data.address2 ?? '',
                city: data.city ?? '',
                stateCode: data.stateCode ?? '',
                postalCode: data.postalCode ?? '',
            });
            setValidationNoMatch(state.noMatch ?? false);
            setValidationSuggestedAddress(state.suggestedAddress);
            setValidationSuggestions(state.suggestions ?? []);
            setValidationModalOpen(true);
        },
        []
    );

    /**
     * Save handler for the AddressModal. When Experian verifyOnSubmit is enabled, performs the
     * same client-side validation gate that the My Account form uses (via useExperianAddress-
     * Validation). Suggestions / no-match outcomes layer the AddressValidationModal on top of
     * the AddressModal; verified-match commits straight through.
     *
     * When verifyOnSubmit is disabled, falls back to the original direct-commit behavior.
     */
    const handleAddressModalSave = async (data: ShopperCustomers.schemas['CustomerAddress']) => {
        if (!verifyOnSubmit) {
            commitAddressModalSave(data);
            return;
        }

        const unspecified: string[] = [];
        if (data.address1) {
            unspecified.push(data.address2 ? `${data.address1}, ${data.address2}` : data.address1);
        }
        const locality = [data.city, data.stateCode, data.postalCode].filter(Boolean).join(', ');
        if (locality) unspecified.push(locality);
        if (unspecified.length === 0) {
            commitAddressModalSave(data);
            return;
        }

        setIsModalValidating(true);
        try {
            const countryIso = data.countryCode === 'CA' ? 'CAN' : 'USA';
            const res = await fetch(VALIDATE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ components: { unspecified }, country_iso: countryIso }),
            });
            if (!res.ok) {
                commitAddressModalSave(data);
                return;
            }
            const json = (await res.json()) as ExperianValidationResult;
            const result = json?.result;
            if (!result) {
                commitAddressModalSave(data);
                return;
            }

            const confidence = result.confidence ?? '';
            const apiAddress = result.address;
            const suggestions = result.suggestions ?? [];
            const suggestedAddress = apiAddress
                ? {
                      address1: apiAddress.address_line_1 ?? '',
                      address2: apiAddress.address_line_2,
                      city: apiAddress.locality,
                      stateCode: apiAddress.region,
                      postalCode: apiAddress.postal_code,
                  }
                : undefined;

            const matches =
                !!suggestedAddress &&
                normalizeAddressField(suggestedAddress.address1) === normalizeAddressField(data.address1) &&
                normalizeAddressField(suggestedAddress.address2) === normalizeAddressField(data.address2) &&
                normalizeAddressField(suggestedAddress.city) === normalizeAddressField(data.city) &&
                normalizeAddressField(suggestedAddress.stateCode) === normalizeAddressField(data.stateCode) &&
                normalizeAddressField(suggestedAddress.postalCode) === normalizeAddressField(data.postalCode);

            if (confidence === 'No matches' || confidence === 'Insufficient search terms') {
                openValidationModalForAddressModal(data, { noMatch: true });
                return;
            }

            if (
                (confidence === 'Verified match' ||
                    confidence === 'Interaction required' ||
                    confidence === 'Verified place' ||
                    confidence === 'Verified street') &&
                suggestedAddress &&
                !matches
            ) {
                openValidationModalForAddressModal(data, { suggestedAddress });
                return;
            }

            if (
                (confidence === 'Multiple matches' ||
                    confidence === 'Premises partial' ||
                    confidence === 'Street partial' ||
                    confidence === 'Too many matches') &&
                suggestions.length > 0
            ) {
                if (confidence === 'Too many matches') {
                    commitAddressModalSave(data);
                    return;
                }
                openValidationModalForAddressModal(data, {
                    suggestions: suggestions.map((s) => ({
                        text: s.text,
                        global_address_key: s.global_address_key,
                    })),
                });
                return;
            }

            commitAddressModalSave(data);
        } catch (error) {
            logger.warn('Experian validation API failed for AddressModal; submitting without modal', { error });
            commitAddressModalSave(data);
        } finally {
            setIsModalValidating(false);
        }
    };

    const handleAddressModalClose = (open: boolean) => {
        if (!open) {
            if (isModalSaving) return;
            setAddressModalOpen(false);
            if (!pendingEditAddressRef.current) setEditingAddress(null);
        }
    };

    const buildFormData = (data: ShippingAddressData, options?: { experianBypass?: boolean; addressId?: string }) => {
        const formData = new FormData();
        // Legacy parity: the persisted phone on the SFCC OrderAddress must be
        // `NNN-NNN-NNNN` (not the `+1 (NNN) NNN-NNNN` presentation form). The
        // `phoneCountryCode` dropdown is UI-only and is stripped here; the
        // digits from the phone field are re-formatted into the legacy shape
        // via `normalizeUsPhone`. The server action also normalizes as
        // defense-in-depth in case a caller ever bypasses this builder.
        const combinedPhone = data.phone ? (normalizeUsPhone(data.phone) ?? '') : '';
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { phoneCountryCode, ...wireRest } = data;
        const wireData: Record<string, unknown> = {
            ...wireRest,
            phone: combinedPhone,
            // Postal-code normalization strips the Experian-suggested `-NNNN`
            // suffix so the persisted `postal-code` on the order is the
            // 5-digit US ZIP (legacy parity).
            postalCode: normalizeUsPostalCode(data.postalCode) ?? data.postalCode,
        };
        Object.entries(wireData).forEach(([key, value]) => {
            if (key === 'smsConsent') {
                // Promotional opt-in — always forward the boolean so the
                // server can stamp the audit trail (true => stamp + source;
                // false => audit-only).
                formData.append('smsConsent', value === true ? 'true' : 'false');
                return;
            }
            if (typeof value === 'boolean') {
                formData.append(key, String(value));
                return;
            }
            if (value) {
                formData.append(key, String(value));
            }
        });
        // One-shot marker: tells the server action to set c_experianBypass on the SCAPI body so
        // the int_sfnext_experian hook skips Experian for this PUT and clears the basket-level
        // pending flag. Only set on the resubmit after the shopper confirms the address in the
        // validation modal.
        if (options?.experianBypass) {
            formData.append('experianBypass', 'true');
        }
        // Identity of an existing saved profile address, propagated through the AddressValidation-
        // Modal Accept resubmit for the SavedAddressesList → Continue flow. Without this the
        // server action falls through to `createCustomerAddress` on the Experian-corrected values,
        // producing a duplicate profile entry (the bug this fix addresses). When set, the server
        // PATCHes the existing profile record in place instead.
        if (options?.addressId) {
            formData.append('addressId', options.addressId);
        }
        return formData;
    };
    const clearValidationModal = () => {
        setValidationModalOpen(false);
        setValidationNoMatch(false);
        setValidationSuggestedAddress(undefined);
        setValidationSuggestions([]);
        setValidationSource(null);
        pendingModalAddressDataRef.current = null;
    };

    /**
     * Called whenever the shopper dismisses the AddressValidationModal WITHOUT accepting
     * either the original or a suggested address. Three surfaces route through here:
     *   1. The explicit "Edit Address" button (`onEdit` prop).
     *   2. The Escape key (Radix Dialog `onOpenChange(false)`).
     *   3. Clicking the backdrop / outside the dialog (also `onOpenChange(false)`).
     *
     * All three represent "go back to the form and keep tweaking." If the shopper then
     * clicks Continue without changing anything, the server will (correctly) return the same
     * `c_experianValidationResult` payload. The dedupe guard in the `useEffect` at line ~614
     * (`validationResult === lastValidationResultRef.current`) would otherwise swallow that
     * second response and leave the shopper stranded on the form with no visible feedback
     * (QA-reported bug). Reset the watermark here so the identical result can re-open the
     * modal on the next submit. The dedupe still protects transient re-renders — those don't
     * run through this path.
     */
    const handleValidationModalDismiss = () => {
        lastValidationResultRef.current = '';
        clearValidationModal();
    };

    /**
     * Adapter for Radix Dialog's `onOpenChange`. Only "close" events (open → false) need the
     * dismissal reset — reopens go through the useEffect at line ~596 which manages state
     * directly. This keeps Escape / backdrop-click behaviorally equivalent to clicking
     * "Edit Address".
     */
    const handleValidationModalOpenChange = (open: boolean) => {
        if (!open) {
            handleValidationModalDismiss();
        }
    };

    const resolveOriginalAddress = useCallback(
        (data: CheckoutActionData | undefined): CheckoutOriginalAddress => {
            const actionAddress =
                data?.data && typeof data.data === 'object'
                    ? (data.data as { address?: ShippingAddressData }).address
                    : undefined;
            if (actionAddress) {
                return {
                    firstName: actionAddress.firstName ?? '',
                    lastName: actionAddress.lastName ?? '',
                    address1: actionAddress.address1 ?? '',
                    address2: actionAddress.address2 ?? '',
                    city: actionAddress.city ?? '',
                    stateCode: actionAddress.stateCode ?? '',
                    postalCode: actionAddress.postalCode ?? '',
                };
            }
            const currentValues = form.getValues();
            return {
                firstName: currentValues.firstName ?? '',
                lastName: currentValues.lastName ?? '',
                address1: currentValues.address1 ?? '',
                address2: currentValues.address2 ?? '',
                city: currentValues.city ?? '',
                stateCode: currentValues.stateCode ?? '',
                postalCode: currentValues.postalCode ?? '',
            };
        },
        [form]
    );

    useEffect(() => {
        if (!verifyOnSubmit || !actionData?.success || actionData.step !== 'shippingAddress') {
            return;
        }

        if (skipNextModalRef.current) {
            skipNextModalRef.current = false;
            clearValidationModal();
            return;
        }

        const validationResult = actionData.basket?.c_experianValidationResult;
        if (typeof validationResult !== 'string' || validationResult.length === 0) {
            clearValidationModal();
            lastValidationResultRef.current = '';
            return;
        }

        // QA-reported bug (checkout → cart → checkout auto-opens the modal):
        // `shippingAddressFetcher` is a KEYED fetcher (`{ key: 'shipping-address-form' }`
        // in use-checkout-actions.ts) whose `.data` persists across component unmount
        // and route navigation. When the shopper accepts "Original" on the modal, the
        // resubmit sends `c_experianBypass=true` and the Experian hook clears the
        // basket's `experianValidation*` state — but the server then returns a
        // `redirect(/cart)` for the ZIP-change verdict, so the fetcher's `.data` is
        // NEVER overwritten with the cleared response. It keeps the earlier
        // modal-triggering payload. On the trip back to /checkout, the component
        // remounts fresh (`lastValidationResultRef.current === ''`), sees the stale
        // keyed-fetcher data, and re-opens the modal without any shopper action.
        //
        // Guard: consult the CURRENT basket (from `useBasket()` — the checkout loader
        // refreshes it on every navigation, so it is the post-redirect source of truth).
        // When the current basket EXPLICITLY reports validation as resolved (result === ''
        // OR pending === false — the exact shape the Experian hook writes on the
        // `c_experianBypass=true` code path), the persisted-fetcher `actionData` is stale
        // and must not re-open the modal.
        //
        // Note: we deliberately reject only on EXPLICIT false / empty-string signals, not
        // on `undefined`. A basket that has never been through the Experian flow (fresh
        // checkout entry, unit-test mocks without these fields) leaves the custom
        // attributes undefined — treating that as "cleared" would suppress the modal on
        // every first submission of the mount session.
        const cartExperian = cart as
            | { c_experianValidationResult?: string; c_experianValidationPending?: boolean }
            | undefined;
        const basketExplicitlyCleared =
            cartExperian?.c_experianValidationResult === '' || cartExperian?.c_experianValidationPending === false;
        if (basketExplicitlyCleared) {
            // Sync the watermark to the cleared basket state so a genuine fresh
            // submission during this mount can still open the modal.
            lastValidationResultRef.current = cartExperian?.c_experianValidationResult ?? '';
            return;
        }

        if (validationResult === lastValidationResultRef.current) {
            return;
        }
        lastValidationResultRef.current = validationResult;

        let parsed: ExperianValidationResult | null = null;
        try {
            parsed = JSON.parse(validationResult) as ExperianValidationResult;
        } catch {
            clearValidationModal();
            return;
        }

        const result = parsed?.result;
        if (!result) {
            clearValidationModal();
            return;
        }

        const confidence = result.confidence ?? '';
        const apiAddress = result.address;
        const suggestions = result.suggestions ?? [];
        const suggestedAddress = apiAddress
            ? {
                  address1: apiAddress.address_line_1 ?? '',
                  address2: apiAddress.address_line_2,
                  city: apiAddress.locality,
                  stateCode: apiAddress.region,
                  postalCode: apiAddress.postal_code,
              }
            : undefined;

        const currentAddress = resolveOriginalAddress(actionData);
        setOriginalAddressSnapshot(currentAddress);

        if (confidence === 'No matches' || confidence === 'Insufficient search terms') {
            setValidationNoMatch(true);
            setValidationSuggestedAddress(undefined);
            setValidationSuggestions([]);
            setValidationModalOpen(true);
            return;
        }

        if (
            (confidence === 'Verified match' ||
                confidence === 'Interaction required' ||
                confidence === 'Verified place' ||
                confidence === 'Verified street') &&
            suggestedAddress &&
            (suggestedAddress.address1 !== currentAddress.address1 ||
                (suggestedAddress.address2 ?? '') !== (currentAddress.address2 ?? '') ||
                (suggestedAddress.city ?? '') !== currentAddress.city ||
                (suggestedAddress.stateCode ?? '') !== (currentAddress.stateCode ?? '') ||
                (suggestedAddress.postalCode ?? '') !== (currentAddress.postalCode ?? ''))
        ) {
            setValidationNoMatch(false);
            setValidationSuggestedAddress(suggestedAddress);
            setValidationSuggestions([]);
            setValidationModalOpen(true);
            return;
        }

        if (
            (confidence === 'Multiple matches' ||
                confidence === 'Premises partial' ||
                confidence === 'Street partial' ||
                confidence === 'Too many matches') &&
            suggestions.length > 0
        ) {
            setValidationNoMatch(false);
            setValidationSuggestedAddress(undefined);
            setValidationSuggestions(
                suggestions.map((s) => ({ text: s.text, global_address_key: s.global_address_key }))
            );
            setValidationModalOpen(true);
            return;
        }

        clearValidationModal();
    }, [actionData, cart, form, resolveOriginalAddress, verifyOnSubmit]);

    /**
     * AddressValidationModal accept handler for the AddressModal flow (Add New / Edit Saved).
     * Uses the cached `pendingModalAddressDataRef` payload rather than the parent form values,
     * so logged-in users with saved addresses still get the right data. Both Add and Edit are
     * routed through `commitAddressModalSave(_, true)` so c_experianBypass flows to whichever
     * SCAPI endpoints the underlying flow hits.
     */
    const handleAddressModalValidationAccept = async (choice: 'original' | 'suggested', suggestedKey?: string) => {
        const cached = pendingModalAddressDataRef.current;
        if (!cached) {
            clearValidationModal();
            return;
        }

        if (choice === 'original') {
            commitAddressModalSave(cached, true);
            clearValidationModal();
            return;
        }

        let nextAddress = validationSuggestedAddress;
        if (suggestedKey) {
            try {
                const response = await fetch(`${FORMAT_URL_PREFIX}${encodeURIComponent(suggestedKey)}`, {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                });
                if (response.ok) {
                    const payload = (await response.json()) as { result?: { address?: Record<string, string> } };
                    const a = payload?.result?.address;
                    if (a) {
                        nextAddress = {
                            address1: a.address_line_1 ?? '',
                            address2: a.address_line_2,
                            city: a.locality,
                            stateCode: a.region,
                            postalCode: a.postal_code,
                        };
                    }
                } else {
                    logger.warn(
                        'Experian format endpoint non-OK response; submitting AddressModal values with bypass',
                        {
                            status: response.status,
                            suggestedKey,
                        }
                    );
                }
            } catch (error) {
                logger.warn('Experian format fetch failed; submitting AddressModal values with bypass', { error });
            }
        }

        const patched: ShopperCustomers.schemas['CustomerAddress'] = nextAddress
            ? {
                  ...cached,
                  address1: nextAddress.address1,
                  address2: nextAddress.address2 ?? '',
                  city: nextAddress.city ?? cached.city ?? '',
                  stateCode: nextAddress.stateCode ?? cached.stateCode ?? '',
                  postalCode: nextAddress.postalCode ?? cached.postalCode ?? '',
              }
            : cached;

        commitAddressModalSave(patched, true);
        clearValidationModal();
    };

    /**
     * PATCHes the picked saved-address profile record with the just-accepted values. Used by
     * `handleValidationModalAccept` (SavedAddressesList → Continue → AddressValidation →
     * Accept) as the PRIMARY write to keep the profile record in sync with what the shopper
     * just accepted on the basket — mirroring what `commitAddressModalSave` does for the
     * AddressModal Edit path.
     *
     * ## Why raw `fetch` instead of `useScapiFetcher`
     *
     * `useScapiFetcher` binds its request URL at hook creation time (memoized on
     * `JSON.stringify(options)`), so a fetcher bound to a stale `effectiveSelectedId` from an
     * earlier render can silently POST against the wrong URL — a real failure mode we hit in
     * production (the shopper's saved profile record was NOT being updated even though the
     * fetcher call was firing, because the memoized fetcher's path did not match the picked
     * tile's `addressId` at the moment of Accept). A raw `fetch` built from the CURRENT
     * `targetAddressId` at call time has no such memoization concerns.
     *
     * ## Why sequential (awaited) rather than fire-and-forget
     *
     * We await the PATCH BEFORE calling `onSubmit` (which fires the shipping-address basket
     * action). This mirrors what the working AddressModal Edit path does — it fires the
     * client PATCH, waits for `applyPendingAddressToBasket` via `useScapiFetcherEffect`, and
     * THEN calls `onSubmit`. Parallel-firing risked a race where the basket action's
     * server-side profile-save branch ran against a stale `getCustomer` snapshot AND the
     * client PATCH might not complete before the shopper navigated away.
     *
     * ## Why not rely solely on the server-side profile-save branch
     *
     * The server-side `submit-shipping-address.server.ts` PATCH depends on SCAPI honoring
     * `expand=[addresses]` on `getCustomer` (see `getCurrentCustomer({ expandAddresses: true })`).
     * In practice that path silently no-op'd in Ashley's environment — the shopper's saved
     * address stayed stale even though the basket picked up the corrected content. The
     * server-side branch stays in place as defense-in-depth for the inline-form flow.
     *
     * ## Failure handling
     *
     * PATCH failure is logged (visible in browser devtools) and the promise still resolves —
     * the shopper must not be blocked from advancing checkout by a best-effort profile sync.
     */
    const patchSelectedSavedAddress = async (
        targetAddress: ShippingAddressData,
        targetAddressId: string,
        targetCustomerId: string
    ): Promise<void> => {
        // Whitelist the payload down to SCAPI-valid CustomerAddress fields (+ `c_*`).
        // Spreading `targetAddress` directly leaked `smsConsent` (which lives on
        // ShippingAddressData because the SMS opt-in checkbox is captured on the
        // shipping-address step) and `phoneCountryCode` into the PATCH body —
        // SCAPI strict-decodes and rejected the request with
        // `HTTP 400: "unknown property 'smsConsent' in document 'CustomerAddress'"`,
        // silently leaving every "SavedAddressesList → Continue → Accept" attempt
        // with a stale bad-ZIP profile record. See `toCustomerAddressBody` doc.
        const body = toCustomerAddressBody({
            ...targetAddress,
            addressId: targetAddressId,
            phone: targetAddress.phone ? (normalizeUsPhone(targetAddress.phone) ?? targetAddress.phone) : '',
            postalCode: normalizeUsPostalCode(targetAddress.postalCode) ?? targetAddress.postalCode ?? '',
            c_experianBypass: true,
        });

        const encoded = encodeResource('shopperCustomers', 'updateCustomerAddress', {
            params: {
                path: {
                    customerId: targetCustomerId,
                    addressName: targetAddressId,
                },
            },
        });
        const url = `${RESOURCE_API_ROUTE}/${encoded}`;

        logger.info('Firing client-side profile PATCH for picked saved address', {
            addressName: targetAddressId,
            customerId: targetCustomerId,
        });

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                logger.warn(
                    'Client-side profile PATCH returned non-OK HTTP status; profile may not have been updated',
                    {
                        status: response.status,
                        addressName: targetAddressId,
                    }
                );
                return;
            }
            // CRITICAL: the resource-api route returns HTTP 200 with `{success: false, errors: [...]}`
            // when the underlying SCAPI call throws — a 200 status alone does NOT mean the PATCH
            // succeeded. We must inspect the JSON body's `success` flag AND surface `errors` to
            // the log so a real failure (auth, validation, hook rejection, permission) is
            // diagnosable in devtools instead of silently swallowed.
            let payload: { success?: boolean; errors?: unknown; data?: unknown } | undefined;
            try {
                payload = (await response.json()) as typeof payload;
            } catch {
                payload = undefined;
            }
            if (payload && payload.success === false) {
                logger.warn('Client-side profile PATCH: SCAPI reported failure; profile was NOT updated', {
                    addressName: targetAddressId,
                    errors: payload.errors,
                });
                return;
            }
            logger.info('Client-side profile PATCH succeeded', {
                addressName: targetAddressId,
                responseAddressId:
                    payload?.data && typeof payload.data === 'object'
                        ? (payload.data as { addressId?: string }).addressId
                        : undefined,
            });
        } catch (error) {
            logger.warn('Client-side profile PATCH threw; profile may not have been updated', {
                error: error instanceof Error ? error.message : String(error),
                addressName: targetAddressId,
            });
        }
    };

    const handleValidationModalAccept = async (choice: 'original' | 'suggested', suggestedKey?: string) => {
        if (validationSource === 'addressModal') {
            await handleAddressModalValidationAccept(choice, suggestedKey);
            return;
        }

        // When the flow that triggered this modal was "picked a saved address from the
        // SavedAddressesList → Continue" (registered shopper with saved addresses), carry
        // the saved address's addressId through the Accept resubmit. The server action's
        // profile-save branch uses it to PATCH the existing CustomerAddress in place
        // instead of creating a duplicate one on the corrected/suggested values.
        // Guest / first-time-saved-address flows (`hasSavedAddresses === false`) submit
        // with no addressId — the server creates the profile record as usual.
        const activeSavedAddressId =
            hasSavedAddresses && savedAddresses.some((a) => a.id === effectiveSelectedId)
                ? effectiveSelectedId
                : undefined;

        // Base payload for the resubmit. Prefer the server-echoed submitted address
        // (`actionData.data.address`) over `form.getValues()` because the two are only
        // equivalent when the shopper originally submitted via the inline form. The
        // SavedAddressesList → Continue path calls `handleSavedAddressSubmit`, which
        // builds FormData directly from the picked saved address and NEVER touches the
        // react-hook-form state — so `form.getValues()` still holds the mount-time
        // defaults (customer preferred address / basket shippingAddress), which for a
        // non-preferred picked address diverge from the address the shopper actually
        // selected. Using stale form values on Accept caused two QA bugs:
        //   (1) Accept Suggested → basket + profile PATCH got the WRONG name / phone
        //       (form defaults instead of the picked address's own values), so the saved
        //       profile record was either silently corrupted or left un-updated.
        //   (2) Accept Original → basket was rewritten with the form-default address
        //       instead of the picked one, so the Experian hash stamped for the "skip
        //       re-validation on unchanged resubmit" fast-path did NOT match the address
        //       the shopper still saw selected in the tile list — the next Continue then
        //       either re-prompted the modal or (when the hashes collided coincidentally)
        //       left the shopper stuck with no visible feedback.
        // The server echo is authoritative because it reflects EXACTLY what was submitted
        // (and validated / normalized) by the request that produced the modal — regardless
        // of whether the source was the inline form or the saved-addresses list.
        const echoedAddress =
            actionData?.data && typeof actionData.data === 'object'
                ? (actionData.data as { address?: ShippingAddressData }).address
                : undefined;
        const baseAddress: ShippingAddressData = echoedAddress ?? form.getValues();

        if (choice === 'original') {
            // Shopper accepted the entered address. Resubmit with experianBypass=true so the
            // server-side hook clears the pending flag and the checkout step advances.
            skipNextModalRef.current = true;
            // Sequential: client PATCH BEFORE the basket resubmit — mirrors the AddressModal
            // Edit path (which chains via `applyPendingAddressToBasket` in the fetcher effect).
            // See `patchSelectedSavedAddress` doc-comment for the full rationale.
            if (activeSavedAddressId && customerId) {
                await patchSelectedSavedAddress(baseAddress, activeSavedAddressId, customerId);
            }
            onSubmit(buildFormData(baseAddress, { experianBypass: true, addressId: activeSavedAddressId }));
            clearValidationModal();
            return;
        }

        skipNextModalRef.current = true;
        let nextAddress = validationSuggestedAddress;
        if (suggestedKey) {
            try {
                const response = await fetch(`${FORMAT_URL_PREFIX}${encodeURIComponent(suggestedKey)}`, {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                });
                if (response.ok) {
                    const payload = (await response.json()) as { result?: { address?: Record<string, string> } };
                    const a = payload?.result?.address;
                    if (a) {
                        nextAddress = {
                            address1: a.address_line_1 ?? '',
                            address2: a.address_line_2,
                            city: a.locality,
                            stateCode: a.region,
                            postalCode: a.postal_code,
                        };
                    }
                } else {
                    logger.warn('Experian format endpoint non-OK response; submitting form values with bypass', {
                        status: response.status,
                        suggestedKey,
                    });
                }
            } catch (error) {
                logger.warn('Experian format fetch failed; submitting form values with bypass', { error });
            }
        }

        // Keep the visible inline form in sync when it is the source (harmless no-op for
        // the saved-address flow — that surface renders SavedAddressesList, not the form).
        if (nextAddress) {
            form.setValue('address1', nextAddress.address1);
            form.setValue('address2', nextAddress.address2 ?? '');
            form.setValue('city', nextAddress.city ?? '');
            form.setValue('stateCode', nextAddress.stateCode ?? '');
            form.setValue('postalCode', nextAddress.postalCode ?? '');
        }

        // Shopper accepted a suggested address. Merge the suggested address fields on top
        // of `baseAddress` so identity fields (name, phone, countryCode) come from what
        // was actually submitted (see comment above), not from stale form defaults. When
        // the format fetch fails or no suggestion was available (multiple-matches branch),
        // fall back to `baseAddress` alone with the bypass marker so the shopper is never
        // stranded behind a closed modal.
        const finalAddress: ShippingAddressData = nextAddress
            ? {
                  ...baseAddress,
                  address1: nextAddress.address1,
                  address2: nextAddress.address2 ?? '',
                  city: nextAddress.city ?? '',
                  stateCode: nextAddress.stateCode ?? '',
                  postalCode: nextAddress.postalCode ?? '',
              }
            : baseAddress;

        // Client-side profile PATCH for the SavedAddressesList Accept flow — QA-reported bug:
        // picking a saved-address tile → Continue → Accept-Suggested updated the basket with
        // the corrected address, but the SAVED profile record kept the old (bad-ZIP) content.
        // Root cause: the server-side profile-save branch depends on SCAPI honoring
        // `expand=[addresses]` on `getCustomer`, which silently no-op'd in Ashley's
        // environment, so the identity match against the picked `addressId` never fired the
        // PATCH. Mirror what the AddressModal Edit path does — it always worked because it
        // fires an explicit CLIENT-side PATCH before the basket action runs.
        //
        // Sequential (awaited) so the profile write completes before the basket resubmit
        // triggers loader revalidation — otherwise the checkout could re-render with a stale
        // profile snapshot before the PATCH lands. See `patchSelectedSavedAddress`
        // doc-comment for the raw-fetch / memoization rationale.
        if (activeSavedAddressId && customerId) {
            await patchSelectedSavedAddress(finalAddress, activeSavedAddressId, customerId);
        }

        onSubmit(buildFormData(finalAddress, { experianBypass: true, addressId: activeSavedAddressId }));

        clearValidationModal();
    };

    const handleSavedAddressSubmit = () => {
        const address = savedAddresses.find((a) => a.id === effectiveSelectedId);
        if (!address) return;
        const formData = addressToFormData(address);
        // Identity signal to the server action so it skips the save-to-profile branch for
        // an already-persisted saved address (defense-in-depth alongside the content-based
        // dedup). Without this, a stale `getCurrentCustomer` snapshot or a drifted address
        // key would produce a duplicate CustomerAddress on the shopper's profile. The Edit
        // path (AddressModal Save) already carries `addressId` through its own FormData
        // build (`commitAddressModalSave`).
        if (address.id) {
            formData.append('addressId', address.id);
        }
        onSubmit(formData);
    };

    const stepTitle =
        hasSavedAddresses && isEditing ? (
            <div className="block sm:flex items-center justify-between w-full">
                <div>{t('shippingAddress.title')}</div>
                <div className="mt-2 sm:mt-0 flex items-center gap-2">
                    {/* @sfdc-extension-block-start SFDC_EXT_MULTISHIP */}
                    {enableMultiAddress && (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="cursor-pointer font-bold"
                            onClick={handleToggleShippingAddressMode}>
                            {tMultiship('checkout.deliverToMultipleAddresses')}
                        </Button>
                    )}
                    {/* @sfdc-extension-block-end SFDC_EXT_MULTISHIP */}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="font-semibold text-secondary-foreground gap-1 font-sans leading-4 tracking-button"
                        onClick={() => setAddressModalOpen(true)}
                        aria-label={t('shippingAddress.newAddressButton')}>
                        <Plus className="size-4" aria-hidden />
                        <span>{t('shippingAddress.newAddressButton')}</span>
                    </Button>
                </div>
            </div>
        ) : (
            <>{t('shippingAddress.title')}</>
        );

    return (
        <ToggleCard
            id="shipping-address"
            title={stepTitle as React.ReactNode}
            editing={isEditing}
            disableEdit={!isCompleted && !isEditing}
            onEdit={onEdit}
            editLabel={t('common.edit')}
            // @sfdc-extension-block-start SFDC_EXT_MULTISHIP
            editAction={
                enableMultiAddress && !hasSavedAddresses ? tMultiship('checkout.deliverToMultipleAddresses') : undefined
            }
            onEditActionClick={enableMultiAddress && !hasSavedAddresses ? handleToggleShippingAddressMode : undefined}
            // @sfdc-extension-block-end SFDC_EXT_MULTISHIP
            isLoading={isLoading}>
            <ToggleCardEdit>
                {hasSavedAddresses ? (
                    <div className="flex flex-col gap-4 pt-2">
                        {shippingFormError && <CheckoutErrorBanner message={shippingFormError} />}
                        {actionData?.fieldErrors && (
                            <div className="space-y-2">
                                {Object.entries(actionData.fieldErrors).map(([field, error]) => (
                                    <CheckoutErrorBanner key={field} message={error} />
                                ))}
                            </div>
                        )}
                        <SavedAddressesList
                            addresses={savedAddresses}
                            value={effectiveSelectedId}
                            onValueChange={setSelectedAddressId}
                            onEditAddress={handleEditAddress}
                        />
                        <Button
                            type="button"
                            disabled={isLoading}
                            className="w-full whitespace-normal lg:w-fit"
                            onClick={handleSavedAddressSubmit}>
                            {isLoading ? t('shippingAddress.saving') : t('shippingAddress.continue')}
                        </Button>
                    </div>
                ) : (
                    <Form {...form}>
                        <form
                            onSubmit={(e) => void form.handleSubmit((data) => onSubmit(buildFormData(data)))(e)}
                            className="flex flex-col gap-4 pt-2">
                            {shippingFormError && <CheckoutErrorBanner message={shippingFormError} />}
                            {actionData?.fieldErrors && (
                                <div className="space-y-2">
                                    {Object.entries(actionData.fieldErrors).map(([field, error]) => (
                                        <CheckoutErrorBanner key={field} message={error} />
                                    ))}
                                </div>
                            )}
                            <AddressFormFields
                                form={form}
                                showPhone={true}
                                phoneRequired={true}
                                showCountry={true}
                                autoFocus={isEditing}
                                autoFocusField="firstName"
                                countryCode={DEFAULT_COUNTRY_CODE}
                            />
                            {/* Promotional SMS opt-in directly under the phone field. Optional —
                                no asterisk and the field is not part of the required-validators
                                in `createShippingAddressSchema`. Wrapped in a UITarget so the
                                consent surface can be replaced or augmented by extensions
                                without forking ShippingAddress (mirrors the prior contact-info
                                placement before phone + consent moved here). */}
                            <UITarget targetId="sfcc.checkout.smsConsent">
                                <SmsConsent control={form.control} />
                            </UITarget>
                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="h-auto whitespace-normal w-full lg:w-fit">
                                {isLoading ? t('shippingAddress.saving') : t('shippingAddress.continue')}
                            </Button>
                        </form>
                    </Form>
                )}
            </ToggleCardEdit>

            <ToggleCardSummary>
                {!isShippingAddressIncomplete(shippingAddress) ? (
                    <ShippingAddressDisplay address={shippingAddress} displayPhone />
                ) : (
                    <p className="text-sm text-muted-foreground">{t('shippingAddress.completePreviousSteps')}</p>
                )}
            </ToggleCardSummary>
            <AddressModal
                showPhone={true}
                // Phone is required at checkout — flips on the asterisk in the label and
                // the schema's `phone.min(1)` validator (state + postal code also flip to
                // strictly required, matching the rest of checkout's shipping-address rules).
                // SMS consent is intentionally NOT rendered here: it's collected only on
                // the guest inline form (alongside the basket's `c_smsOptIn` persistence
                // in submit-shipping-address.server.ts). Logged-in shoppers who add or
                // edit a saved address through this modal don't see the opt-in.
                strictValidation={true}
                open={addressModalOpen}
                onOpenChange={handleAddressModalClose}
                isEditMode={isEditMode}
                countryCode={editingAddress?.countryCode ?? DEFAULT_COUNTRY_CODE}
                defaultValues={editingAddress ? { ...editingAddress, addressId: editingAddress.id } : undefined}
                onSave={(data) => void handleAddressModalSave(data)}
                isLoading={!!isModalSaving}
            />
            {verifyOnSubmit && (
                <AddressValidationModal
                    open={validationModalOpen}
                    onOpenChange={handleValidationModalOpenChange}
                    originalAddress={originalAddressSnapshot}
                    suggestedAddress={validationSuggestedAddress}
                    noMatch={validationNoMatch}
                    suggestions={validationSuggestions}
                    onAccept={(choice, suggestedKey) => void handleValidationModalAccept(choice, suggestedKey)}
                    onEdit={handleValidationModalDismiss}
                />
            )}
        </ToggleCard>
    );
}
